import { Component, Vue } from "vue-property-decorator";
import WithRender from "./SenderNameLengthComplianceDetails.html";
import { Inject } from "vue-di-container";
import RequestsSendersService from "../RequestsSendersService";
import { Toaster } from "@/Services/toast";
import { SendersDetailsFilterModel } from "../SendersDetailsFilterModel";
import {
    assignSenderNameLengthComplianceRowKeys,
    buildSenderNameLengthComplianceScopePayload,
    downloadExcelFromBase64,
    EMPTY_GUID,
    formatCreatedDate,
    getTodayStr,
    loadSendersFilterFromStorage,
} from "../sendersDetailsShared";

@WithRender
@Component({
    diProvide: [RequestsSendersService],
})
export default class SenderNameLengthComplianceDetails extends Vue {
    @Inject(RequestsSendersService)
    public requestsSendersService!: RequestsSendersService;

    public isLoading: boolean = false;
    public rows: any[] = [];
    public totalCount: number = 0;
    public clientItems: string[] = [];
    public senderNameFilter: string = "";
    public clientNameFilter: string = "";
    public senderNameLengthFilter: number | null = null;
    public senderNameLengthItems: { text: string; value: number | null }[] = [];

    public pagination: any = {
        page: 1,
        rowsPerPage: 10,
        sortBy: "createdDate",
        descending: true,
    };
    public isSorted: boolean = true;
    public headers: any[] = [];

    private sendersFilter: SendersDetailsFilterModel = {
        dateFrom: "2022-02-01",
        dateTo: "",
        tenantId: EMPTY_GUID,
        providerId: EMPTY_GUID,
        customerType: 0,
        activeNow: false,
    };

    private chartNameLength: number | null = null;
    private searchDebounceId: any = null;

    public clientNameMenuProps: Record<string, unknown> = {
        contentClass: "monitor-dashboard-select-menu all-requests-details-client-menu",
        offsetY: true,
        maxHeight: 260,
    };

    public created(): void {
        this.initHeaders();
        this.initSenderNameLengthItems();
        loadSendersFilterFromStorage(this.sendersFilter);
        this.applyRouteNameLengthDrillDown();
    }

    public async mounted(): Promise<void> {
        if (!this.sendersFilter.dateTo) {
            this.sendersFilter.dateTo = getTodayStr();
        }
        await this.loadClients();
        await this.loadTable();
    }

    public initSenderNameLengthItems(): void {
        const items: { text: string; value: number | null }[] = [];
        for (let length = 2; length <= 11; length++) {
            items.push({
                text: String(length),
                value: length,
            });
        }
        this.senderNameLengthItems = items;
    }

    public initHeaders(): void {
        this.headers = [
            {
                text: this.$t("monitorDashboard.createdDate"),
                value: "createdDate",
                align: "center",
                sortable: true,
                width: "160",
            },
            {
                text: this.$t("monitorDashboard.customerType"),
                value: "customerType",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.senderName"),
                value: "senderName",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.clientName"),
                value: "clientName",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.smsServiceProvider"),
                value: "smsServiceProvider",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.serviceProvider"),
                value: "serviceProvider",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.senderNameLengthColumn"),
                value: "senderNameLength",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.isCompliant"),
                value: "isCompliant",
                align: "center",
                sortable: false,
            },
        ];
    }

    private applyRouteNameLengthDrillDown(): void {
        const q = this.$route.query;
        if (!q) {
            return;
        }

        const lengthRaw = q.nameLength != null ? String(q.nameLength) : "";
        const nameLength = lengthRaw ? Number(lengthRaw) : NaN;

        if (!isNaN(nameLength) && nameLength >= 2 && nameLength <= 11) {
            this.chartNameLength = nameLength;
            this.senderNameLengthFilter = nameLength;
        }
    }

    private buildScopePayload(): any {
        return buildSenderNameLengthComplianceScopePayload(
            this.sendersFilter,
            this.chartNameLength,
        );
    }

    private resolveNameLengthFilter(): number | null {
        if (this.senderNameLengthFilter != null) {
            return this.senderNameLengthFilter;
        }
        return this.chartNameLength;
    }

    private buildDetailsPayload(): any {
        const payload: any = this.buildScopePayload();
        payload.senderName = this.senderNameFilter ? this.senderNameFilter.trim() : "";
        payload.clientName = this.clientNameFilter ? this.clientNameFilter.trim() : "";
        payload.pageIndex = (this.pagination.page - 1) >= 0 ? this.pagination.page - 1 : 0;
        payload.pageSize = this.pagination.rowsPerPage || 10;
        payload.sortBy = "CreatedDate";
        payload.sortDirection = this.pagination.descending === true;

        const nameLengthFilter = this.resolveNameLengthFilter();
        if (nameLengthFilter != null) {
            payload.senderNameLength = nameLengthFilter;
        }

        return payload;
    }

    public async loadClients(): Promise<void> {
        const resp = await this.requestsSendersService.GetSenderNameLengthComplianceDetailsClients(
            this.buildScopePayload(),
        );
        if (!resp || !resp.isSuccess || !resp.result) {
            this.clientItems = [];
            return;
        }
        this.clientItems = resp.result;
    }

    public async loadTable(): Promise<void> {
        this.isLoading = true;
        const resp = await this.requestsSendersService.GetSenderNameLengthComplianceDetails(
            this.buildDetailsPayload(),
        );
        this.isLoading = false;

        if (!resp || !resp.isSuccess) {
            if (resp && resp.errorMessage) {
                Toaster.error(resp.errorMessage);
            }
            this.rows = [];
            this.totalCount = 0;
            return;
        }

        this.rows = assignSenderNameLengthComplianceRowKeys(resp.result || []);
        this.totalCount = resp.totalCount != null ? Number(resp.totalCount) : 0;
    }

    public onSenderNameInput(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
        }
        const self = this;
        this.searchDebounceId = setTimeout(function () {
            self.pagination.page = 1;
            self.loadTable();
        }, 800);
    }

    public onClientNameChange(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
            this.searchDebounceId = null;
        }
        this.pagination.page = 1;
        this.loadTable();
    }

    public onClientNameKeyup(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
        }
        const self = this;
        this.searchDebounceId = setTimeout(function () {
            self.pagination.page = 1;
            self.loadTable();
        }, 800);
    }

    public onSenderNameLengthChange(): void {
        this.pagination.page = 1;
        this.loadTable();
    }

    public async resetLocalFilters(): Promise<void> {
        this.senderNameFilter = "";
        this.clientNameFilter = "";
        this.senderNameLengthFilter = null;
        this.pagination.page = 1;
        await this.loadTable();
    }

    public async onExport(): Promise<void> {
        const data = await this.requestsSendersService.ExportSenderNameLengthComplianceDetails(
            this.buildDetailsPayload(),
        );
        if (!data || !data.isSuccess || !data.result) {
            if (data && data.errorMessage) {
                Toaster.error(data.errorMessage);
            }
            return;
        }
        downloadExcelFromBase64(data.result, "SenderNameLengthComplianceDetails.xlsx");
    }

    public goBack(): void {
        this.$router.push({
            name: "MonitorDashboard",
            query: {
                topTab: "0",
                requestsTab: "1",
            },
        });
    }

    public changeSort(column: string): void {
        if (column !== "createdDate") {
            return;
        }
        if (this.pagination.sortBy === column) {
            this.pagination.descending = !this.pagination.descending;
        } else {
            this.pagination.sortBy = column;
            this.pagination.descending = true;
        }
        this.isSorted = true;
        this.loadTable();
    }

    public async updatePaginate(val: any): Promise<void> {
        this.pagination.page = val.page;
        this.pagination.rowsPerPage = val.rowsPerPage;
        await this.loadTable();
    }

    public formatCreatedDate(value: string): string {
        return formatCreatedDate(value);
    }
}
